import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';

const JsonBeautifierPage: React.FC = () => {
  const [inputJson, setInputJson] = useState('');
  const [outputJson, setOutputJson] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  const beautifyJson = () => {
    try {
      if (!inputJson.trim()) {
        setError('Please enter JSON to beautify');
        setOutputJson('');
        return;
      }

      const parsed = JSON.parse(inputJson);
      const beautified = JSON.stringify(parsed, null, 2); // 2 spaces for indentation
      setOutputJson(beautified);
      setError('');
    } catch (err) {
      setError('Invalid JSON format. Please check your input.');
      setOutputJson('');
    }
  };

  const minifyJson = () => {
    try {
      if (!inputJson.trim()) {
        setError('Please enter JSON to minify');
        setOutputJson('');
        return;
      }

      const parsed = JSON.parse(inputJson);
      const minified = JSON.stringify(parsed);
      setOutputJson(minified);
      setError('');
    } catch (err) {
      setError('Invalid JSON format. Please check your input.');
      setOutputJson('');
    }
  };

  const validateJson = () => {
    try {
      if (!inputJson.trim()) {
        setError('Please enter JSON to validate');
        setOutputJson(''); // Clear previous output
        return;
      }

      JSON.parse(inputJson);
      setError(''); // Clear any previous error
      setOutputJson('✅ Valid JSON format');
    } catch (err) {
      // It's good practice to show the actual error message for better debugging
      // For a user-facing message, 'Invalid JSON format' is fine.
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`❌ Invalid JSON format: ${errorMessage}`);
      setOutputJson('');
    }
  };

  const copyToClipboard = async () => {
    if (!outputJson || outputJson === '✅ Valid JSON format' || outputJson.startsWith('❌ Invalid JSON format')) {
      // Avoid copying status messages
      setCopySuccess(false);
      return;
    }
    try {
      await navigator.clipboard.writeText(outputJson);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setError('Failed to copy to clipboard.');
    }
  };

  const clearAll = () => {
    setInputJson('');
    setOutputJson('');
    setError('');
    setCopySuccess(false);
  };

  const loadSampleJson = () => {
    const sample = {
      "name": "John Doe",
      "age": 30,
      "address": {
        "street": "123 Main St",
        "city": "New York",
        "zipCode": "10001"
      },
      "hobbies": ["reading", "swimming", "coding"],
      "isActive": true,
      "metadata": null
    };
    setInputJson(JSON.stringify(sample, null, 2)); // Load sample beautified
    setOutputJson(''); // Clear output
    setError(''); // Clear error
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
      <Card elevation={3}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FormatAlignJustifyIcon sx={{ mr: 1.5, fontSize: { xs: 28, sm: 32 }, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              JSON Beautifier
            </Typography>
          </Box>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Format, minify, and validate your JSON data. Paste your JSON into the input field and use the buttons below.
          </Typography>

          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* Input Section */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">Input JSON</Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={loadSampleJson}
                  >
                    Load Sample
                  </Button>
                </Box>
                
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  value={inputJson}
                  onChange={(e) => setInputJson(e.target.value)}
                  placeholder="Paste your JSON here..."
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-root': { height: '100%' },
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      height: 'calc(100% - 20px) !important', // Adjust based on padding
                      overflowY: 'auto !important'
                    }
                  }}
                />
              </Paper>
            </Grid>

            {/* Output Section */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">Output</Typography>
                  {outputJson && !outputJson.startsWith('✅') && !outputJson.startsWith('❌') && (
                    <Tooltip title="Copy to clipboard">
                      <IconButton onClick={copyToClipboard} size="small">
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  value={outputJson}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  placeholder="Result will appear here..."
                  sx={{
                    '& .MuiInputBase-root': { height: '100%' },
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      height: 'calc(100% - 20px) !important', // Adjust based on padding
                      overflowY: 'auto !important',
                      backgroundColor: outputJson.startsWith('✅') ? 'rgba(76, 175, 80, 0.1)' : outputJson.startsWith('❌') ? 'rgba(244, 67, 54, 0.1)' : 'inherit',
                    }
                  }}
                />
              </Paper>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={beautifyJson}
              disabled={!inputJson.trim()}
              sx={{ flexGrow: 1, minWidth: '120px' }}
            >
              Beautify
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={minifyJson}
              disabled={!inputJson.trim()}
              sx={{ flexGrow: 1, minWidth: '120px' }}
            >
              Minify
            </Button>
            <Button
              variant="outlined"
              onClick={validateJson}
              disabled={!inputJson.trim()}
              sx={{ flexGrow: 1, minWidth: '120px' }}
            >
              Validate
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={clearAll}
              startIcon={<ClearIcon />}
              sx={{ flexGrow: 1, minWidth: '120px' }}
            >
              Clear All
            </Button>
          </Box>

          {/* Status Messages */}
          {error && !outputJson.startsWith('❌') && ( // Avoid double error display if output also shows it
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          
          {copySuccess && (
            <Alert severity="success" sx={{ mt: 2, position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1500 }}>
              Copied to clipboard!
            </Alert>
          )}

          {/* Features Info */}
          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Key Features:
            </Typography>
            <ul style={{ paddingLeft: 20, marginTop: 0 }}>
              <li>
                <strong>Beautify:</strong> Formats your JSON data with consistent indentation and line breaks, making it human-readable.
              </li>
              <li>
                <strong>Minify:</strong> Compresses your JSON data by removing all unnecessary whitespace, ideal for transmission or storage.
              </li>
              <li>
                <strong>Validate:</strong> Checks your JSON data for syntax errors, ensuring it conforms to the JSON specification.
              </li>
              <li>
                <strong>Copy Output:</strong> Quickly copy the processed JSON to your clipboard.
              </li>
              <li>
                <strong>Load Sample:</strong> Instantly load a sample JSON object to test the features.
              </li>
              <li>
                <strong>Clear All:</strong> Reset both input and output fields.
              </li>
            </ul>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default JsonBeautifierPage;
